import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface Category {
  id: string;
  name: string;
}

export function CategoryConfig() {
  const [categories, setCategories] = useState<Category[]>([
    { id: "1", name: "Barras Metálicas" },
    { id: "2", name: "Chapas de Aço" },
    { id: "3", name: "Tubos Metálicos" },
    { id: "4", name: "Telas e Grades" },
  ]);
  const [newCategory, setNewCategory] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleAdd = () => {
    if (!newCategory.trim()) {
      toast({
        title: "Erro",
        description: "O nome da categoria não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    if (categories.some(cat => cat.name.toLowerCase() === newCategory.toLowerCase())) {
      toast({
        title: "Erro",
        description: "Já existe uma categoria com este nome.",
        variant: "destructive",
      });
      return;
    }

    setCategories([...categories, { id: Date.now().toString(), name: newCategory }]);
    setNewCategory("");
    toast({
      title: "Sucesso",
      description: "Categoria adicionada com sucesso.",
    });
  };

  const handleEdit = (id: string, newName: string) => {
    if (!newName.trim()) {
      toast({
        title: "Erro",
        description: "O nome da categoria não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    if (categories.some(cat => cat.name.toLowerCase() === newName.toLowerCase() && cat.id !== id)) {
      toast({
        title: "Erro",
        description: "Já existe uma categoria com este nome.",
        variant: "destructive",
      });
      return;
    }

    setCategories(categories.map(cat => 
      cat.id === id ? { ...cat, name: newName } : cat
    ));
    setEditingId(null);
    toast({
      title: "Sucesso",
      description: "Categoria atualizada com sucesso.",
    });
  };

  const handleDelete = (id: string) => {
    setCategories(categories.filter(cat => cat.id !== id));
    toast({
      title: "Sucesso",
      description: "Categoria removida com sucesso.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <Input
          placeholder="Nova categoria"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          className="max-w-sm bg-[#222831] text-white border-[#404040]"
        />
        <Button onClick={handleAdd} className="bg-[#0073B7] hover:bg-[#004080] text-white">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>
      </div>

      <div className="bg-[#1A1F2C] rounded-md border border-[#404040]">
        <div className="divide-y divide-[#404040]">
          {categories.map((category) => (
            <div key={category.id} className="p-4 flex items-center justify-between text-white hover:bg-[#222831]">
              {editingId === category.id ? (
                <Input
                  defaultValue={category.name}
                  onBlur={(e) => handleEdit(category.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEdit(category.id, e.currentTarget.value);
                    }
                  }}
                  className="max-w-sm bg-[#222831] text-white border-[#404040]"
                  autoFocus
                />
              ) : (
                <span>{category.name}</span>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingId(category.id)}
                  className="hover:bg-[#222831]"
                >
                  <Pencil className="h-4 w-4 text-[#0073B7]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(category.id)}
                  className="hover:bg-[#222831]"
                >
                  <Trash2 className="h-4 w-4 text-[#E60026]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}